/**
 * Guide Bookings Page
 *
 * Allows guides to view and manage their bookings with filtering capabilities.
 * Displays bookings in card format with customer info, dates, and quick actions.
 *
 * Epic: MVP 1.21.5 - Dashboard Completion
 * Story: 2.1 - Guide Bookings Page
 *
 * @version 1.0
 * @created 2026-01-17
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import { formatCentsToDollars } from '../../utils/formHelpers';
import DashboardLayout from '../../components/layout/DashboardLayout';
import StatusBadge from '../../components/common/StatusBadge';
import Alert from '../../components/common/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import ConfirmModal from '../../components/shared/ConfirmModal';
import BookingCancellationGuide from '../../components/guide/BookingCancellationGuide';
import DisputeButton from '../../components/booking/DisputeButton';
import {
  Calendar,
  Users as _Users,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  Eye,
  AlertCircle
} from 'lucide-react';

export default function GuideBookings() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [declineConfirm, setDeclineConfirm] = useState({ isOpen: false, bookingId: null });
  const [cancelBookingModal, setCancelBookingModal] = useState({ isOpen: false, bookingId: null });

  // Fetch bookings on mount and filter change
  useEffect(() => {
    fetchBookings(activeFilter);
  }, [activeFilter]);

  const fetchBookings = async (status) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest(`/api/v1/bookings/guide?status=${status}`, {
        method: 'GET'
      });

      if (response.success) {
        setBookings(response.data);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleViewDetails = (bookingId) => {
    navigate(`/guide/bookings/${bookingId}`);
  };

  const handleAcceptBooking = async (bookingId) => {
    try {
      const response = await apiRequest(`/api/v1/bookings/${bookingId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: 'confirmed' })
      });

      if (response.success) {
        // Refresh bookings list
        fetchBookings(activeFilter);
      }
    } catch (err) {
      console.error('Error accepting booking:', err);
      setError('Failed to accept booking. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDeclineBooking = (bookingId) => {
    setDeclineConfirm({ isOpen: true, bookingId });
  };

  const executeDeclineBooking = async (bookingId) => {
    setDeclineConfirm({ isOpen: false, bookingId: null });

    try {
      const response = await apiRequest(`/api/v1/bookings/${bookingId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: 'cancelled' })
      });

      if (response.success) {
        // Refresh bookings list
        fetchBookings(activeFilter);
      }
    } catch (err) {
      console.error('Error declining booking:', err);
      setError('Failed to decline booking. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = formatCentsToDollars;

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', variant: 'warning', icon: AlertCircle },
      confirmed: { label: 'Confirmed', variant: 'success', icon: CheckCircle },
      paid: { label: 'Paid', variant: 'info', icon: DollarSign },
      in_progress: { label: 'In Progress', variant: 'info', icon: Clock },
      completed: { label: 'Completed', variant: 'neutral', icon: CheckCircle },
      cancelled: { label: 'Cancelled', variant: 'error', icon: XCircle },
      refunded: { label: 'Refunded', variant: 'warning', icon: DollarSign }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <StatusBadge variant={config.variant}>
        <Icon size={14} />
        {config.label}
      </StatusBadge>
    );
  };

  const filterTabs = [
    { id: 'all', label: 'All Bookings' },
    { id: 'pending', label: 'Pending' },
    { id: 'upcoming', label: 'Upcoming' },
    { id: 'past', label: 'Past' }
  ];

  return (
    <DashboardLayout
      userRole="guide"
      activeNavId="bookings"
      onNavigate={(id, path) => path && navigate(path)}
      user={{
        name: 'Guide User',
        email: 'guide@outdoorguides.com',
        avatar: null
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        {/* Page Header */}
        <div>
          <h1 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--space-2)'
          }}>
            My Bookings
          </h1>
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)'
          }}>
            Manage your upcoming and past bookings
          </p>
        </div>

        {/* Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          borderBottom: '1px solid var(--color-border)',
          paddingBottom: 'var(--space-2)'
        }}>
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleFilterChange(tab.id)}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: activeFilter === tab.id ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)',
                color: activeFilter === tab.id ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeFilter === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <LoadingSpinner message="Loading bookings..." />
        )}

        {/* Error State */}
        {error && (
          <Alert variant="error" message={error} />
        )}

        {/* Empty State */}
        {!loading && !error && bookings.length === 0 && (
          <EmptyState
            icon={Calendar}
            title="No bookings found"
            description={
              activeFilter === 'all' ? 'You don\'t have any bookings yet.'
              : activeFilter === 'pending' ? 'No pending bookings at the moment.'
              : activeFilter === 'upcoming' ? 'No upcoming bookings scheduled.'
              : 'No past bookings to display.'
            }
          />
        )}

        {/* Bookings List */}
        {!loading && !error && bookings.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {bookings.map((booking) => (
              <div
                key={booking.id}
                style={{
                  padding: 'var(--space-6)',
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-sm)'
                }}
              >
                {/* Booking Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 'var(--space-4)'
                }}>
                  <div>
                    <h3 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-text-primary)',
                      marginBottom: 'var(--space-2)'
                    }}>
                      {booking.experience_title}
                    </h3>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-secondary)'
                    }}>
                      Booking #{booking.booking_number}
                    </p>
                  </div>
                  {getStatusBadge(booking.status)}
                </div>

                {/* Booking Details Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 'var(--space-4)',
                  marginBottom: 'var(--space-4)',
                  paddingBottom: 'var(--space-4)',
                  borderBottom: '1px solid var(--color-border)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Calendar size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                      {formatDate(booking.start_date)}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <_Users size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                      {booking.customer_first_name} {booking.customer_last_name}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <_Users size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                      {booking.participant_count} participant{booking.participant_count !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <DollarSign size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                      {formatCurrency(booking.total_price_cents)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleViewDetails(booking.id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-4)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--color-primary)',
                      backgroundColor: 'transparent',
                      border: '1px solid var(--color-primary)',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Eye size={16} />
                    View Details
                  </button>

                  {booking.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleAcceptBooking(booking.id)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-4)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--color-white)',
                          backgroundColor: 'var(--color-success)',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <CheckCircle size={16} />
                        Accept
                      </button>

                      <button
                        onClick={() => handleDeclineBooking(booking.id)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-4)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--color-white)',
                          backgroundColor: 'var(--color-error)',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <XCircle size={16} />
                        Decline
                      </button>
                    </>
                  )}

                  {['confirmed', 'paid'].includes(booking.status) && (
                    <button
                      onClick={() => setCancelBookingModal({ isOpen: true, bookingId: booking.id })}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-2) var(--space-4)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--color-error)',
                        backgroundColor: 'transparent',
                        border: '1px solid var(--color-error)',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <XCircle size={16} />
                      Cancel Booking
                    </button>
                  )}

                  {booking.status === 'cancelled' && (
                    <DisputeButton
                      bookingId={booking.id}
                      cancellationId={booking.cancellation_id}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={declineConfirm.isOpen}
        onClose={() => setDeclineConfirm({ isOpen: false, bookingId: null })}
        onConfirm={() => executeDeclineBooking(declineConfirm.bookingId)}
        title="Decline Booking"
        message="Are you sure you want to decline this booking?"
        confirmText="Decline"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      <BookingCancellationGuide
        isOpen={cancelBookingModal.isOpen}
        onClose={() => setCancelBookingModal({ isOpen: false, bookingId: null })}
        bookingId={cancelBookingModal.bookingId || ''}
        onCancelled={() => fetchBookings(activeFilter)}
      />
    </DashboardLayout>
  );
}
